import { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { NotificationProvider } from './contexts/NotificationContext';
import ToastContainer from './components/ui/ToastContainer';
import CircularProgress from './components/ui/CircularProgress';
import Footer from './components/Footer';
import Header from './components/Header';
import QuoteForm from './components/QuoteForm';
import CarrierRegistrationForm from './components/CarrierRegistrationForm';
import AuthModal from './components/AuthModal';
import AuthRequiredAlert from './components/ui/AuthRequiredAlert';
import PaymentSection from './components/PaymentSection';
import ScrollIndicator from './components/ScrollIndicator';
// import ScrollToTop from './components/ScrollToTop'; // Removido: botón flotante de ir hacia arriba
import SessionErrorHandler from './components/SessionErrorHandler';
import HomeChatButton from './components/chat/HomeChatButton';
import HomeChatManager from './components/chat/HomeChatManager';
import FloatingCCPButton from './components/admin/FloatingCCPButton';
import EmbeddedCCPPanel from './components/admin/EmbeddedCCPPanel';
import { supabase } from './lib/supabaseClient';
import './utils/sessionCleaner'; // Cargar utilidades de limpieza de sesión
import { reactQueryConfig } from './hooks/useReactQuery';

// Lazy load main page components
const AboutUs = lazy(() => import('./components/AboutUs'));
const Services = lazy(() => import('./components/Services'));
const VideoSection = lazy(() => import('./components/VideoSection'));
const QuoteSection = lazy(() => import('./components/QuoteSection'));
const ReviewsSection = lazy(() => import('./components/ReviewsSection'));
const CarrierSection = lazy(() => import('./components/CarrierSection'));
const ReadySection = lazy(() => import('./components/ReadySection'));
const FAQ = lazy(() => import('./components/FAQ'));

// Lazy load secondary pages
const DOTNumber = lazy(() => import('./components/DOTNumber'));
const FMCSA = lazy(() => import('./components/FMCSA'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./components/TermsConditions'));
const Contact = lazy(() => import('./components/Contact'));
const AdminRouter = lazy(() => import('./components/admin/AdminRouter'));
const AnalyticsRouter = lazy(
  () => import('./components/analytics/AnalyticsRouter'),
);
const ProtectedQuotePortal = lazy(
  () => import('./components/ProtectedQuotePortal'),
);
const ProtectedCarrierPortal = lazy(
  () => import('./components/ProtectedCarrierPortal'),
);
const NotFound = lazy(() => import('./pages/NotFound'));
const Success = lazy(() => import('./pages/success'));
const Cancel = lazy(() => import('./pages/cancel'));
const QuoteReceived = lazy(() => import('./pages/quote-received'));

// Create a client
const queryClient = new QueryClient(reactQueryConfig);

function App() {
  const location = useLocation();
  const isHomeRoute = location.pathname === '/';
  const [modal, setModal] = useState<{
    type: 'quote' | 'carrier' | 'auth' | 'auth-required' | null;
    step?: 'form' | 'payment';
    recordId?: string;
    authRequiredFor?: 'quote' | 'carrier';
  }>({ type: null, step: 'form' });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [_userProfile, setUserProfile] = useState<any>(null);
  const [_profileLoading, setProfileLoading] = useState(true);
  const [isAdminOrAgent, setIsAdminOrAgent] = useState(false);
  const [ccpOpen, setCcpOpen] = useState(false);

  const terminateCCP = () => {
    try {
      (window as any).connect?.core?.terminate?.();
    } catch (error) {
      console.warn('Error terminating CCP in App:', error);
    }
  };

  // Auto-clear success message
  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 2500);
    return () => clearTimeout(t);
  }, [successMessage]);

  // Debug: log cuando cambia isAdminOrAgent (eliminar después)
  useEffect(() => {
    console.log('CCP Button visibility:', {
      isAdminOrAgent,
      user: !!user,
      profileLoading: _profileLoading,
    });
  }, [isAdminOrAgent, user, _profileLoading]);

  // If user loses admin/agent privileges, force-close and terminate CCP.
  useEffect(() => {
    if (!isAdminOrAgent && ccpOpen) {
      setCcpOpen(false);
      terminateCCP();
    }
  }, [isAdminOrAgent, ccpOpen]);

  const openModal = async (type: 'quote' | 'carrier' | 'auth') => {
    // Si es el modal de auth, abrirlo directamente
    if (type === 'auth') {
      setModal({ type, step: 'form' });
      return;
    }

    // Para quote y carrier, verificar autenticación primero
    const session = await supabase.auth.getSession();
    if (!session.data.session) {
      // Si no está autenticado, mostrar el alert de autenticación
      setModal({ type: 'auth-required', step: 'form', authRequiredFor: type });
      return;
    }

    // Si está autenticado, abrir el modal del formulario
    setModal({ type, step: 'form' });
  };

  const closeModal = () => setModal({ type: null, step: 'form' });

  const handleFormSuccess = (type: 'quote' | 'carrier') => () => {
    if (type === 'quote') {
      // Para quotes, cerrar modal y redirigir a página de confirmación
      closeModal();
      window.location.href = '/quote-received';
    } else {
      // Para carriers, el registro es gratuito. Cerrar modal y mostrar mensaje.
      closeModal();
      setSuccessMessage(
        'Thank you for registering as a carrier! Registration is completely free.',
      );
    }
  };

  // Close modal on ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeModal();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Supabase auth & profile
  useEffect(() => {
    let mounted = true;
    const fetchProfile = async (id: string) => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', id)
          .single();
        if (!error && mounted) {
          setUserProfile(data);
          // Check if user is admin or agent for CCP button
          setIsAdminOrAgent(data?.role === 'admin' || data?.role === 'agent');
        } else if (error) {
          console.error('Error fetching profile:', error);
        }
      } finally {
        if (mounted) setProfileLoading(false);
      }
    };
    const checkProfile = async () => {
      const { data } = await supabase.auth.getUser();
      const u = data.user;
      if (u) {
        const fullName =
          u.user_metadata?.full_name ||
          u.user_metadata?.name ||
          u.user_metadata?.display_name ||
          '';

        // ON CONFLICT DO NOTHING — avoids 409 races and never overwrites role (e.g. admin)
        await supabase.from('profiles').upsert(
          {
            id: u.id,
            full_name: fullName,
            email: u.email,
            role: 'user',
          },
          { onConflict: 'id', ignoreDuplicates: true },
        );

        await fetchProfile(u.id);
      }
    };
    supabase.auth.getUser().then(({ data }) => {
      if (mounted && data.user) {
        setUser(data.user);
        checkProfile();
      } else if (mounted) {
        setProfileLoading(false);
      }
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_e, sess) => {
      if (!mounted) return;
      if (sess?.user) {
        setUser(sess.user);
        setProfileLoading(true);
        checkProfile();
      } else {
        setCcpOpen(false);
        terminateCCP();
        setUser(null);
        setUserProfile(null);
        setIsAdminOrAgent(false);
        setProfileLoading(false);
        setModal({ type: null, step: 'form' });
      }
    });
    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const isEmailVerified = Boolean(
    user?.email_confirmed_at || user?.confirmed_at,
  );

  return (
    <QueryClientProvider client={queryClient}>
      <NotificationProvider>
        <SessionErrorHandler>
          <div className="flex flex-col min-h-screen text-center bg-gray-100 text-gray-800">
            <ScrollIndicator />
            <Header onLoginClick={() => openModal('auth')} />

            <main className="flex-grow">
              <Routes>
                <Route
                  path="/"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <AboutUs />
                      <Services />
                      <VideoSection />
                      <QuoteSection open={openModal} />
                      <ReviewsSection />
                      <CarrierSection open={openModal} />
                      <FAQ />
                      <ReadySection open={openModal} />
                      {/* Contact ya no va aquí */}
                    </Suspense>
                  }
                />
                <Route
                  path="/contact"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <Contact />
                    </Suspense>
                  }
                />
                <Route
                  path="/success"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <Success />
                    </Suspense>
                  }
                />
                <Route
                  path="/cancel"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <Cancel />
                    </Suspense>
                  }
                />
                <Route
                  path="/quote-received"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <QuoteReceived />
                    </Suspense>
                  }
                />
                <Route
                  path="/dot"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <DOTNumber />
                    </Suspense>
                  }
                />
                <Route
                  path="/fmcsa"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <FMCSA />
                    </Suspense>
                  }
                />
                <Route
                  path="/privacy"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <PrivacyPolicy />
                    </Suspense>
                  }
                />
                <Route
                  path="/terms"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <TermsConditions />
                    </Suspense>
                  }
                />
                <Route
                  path="/my-quotes"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <ProtectedQuotePortal />
                    </Suspense>
                  }
                />
                <Route
                  path="/carrier-portal"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <ProtectedCarrierPortal />
                    </Suspense>
                  }
                />
                <Route
                  path="/dashboard/*"
                  element={<Navigate to="/admin/quotes" replace />}
                />
                {/* Rutas de admin - solo accesibles para admins */}
                <Route
                  path="/admin/*"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <AdminRouter />
                    </Suspense>
                  }
                />
                <Route
                  path="/analytics/*"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <AnalyticsRouter />
                    </Suspense>
                  }
                />
                {/* 404 - Debe ir al final para capturar todas las rutas no encontradas */}
                <Route
                  path="*"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <CircularProgress size="lg" />
                        </div>
                      }
                    >
                      <NotFound />
                    </Suspense>
                  }
                />
              </Routes>
            </main>

            <Footer />
            {/* ScrollToTop removido - botón flotante de ir hacia arriba eliminado */}
            <AnimatePresence mode="wait">
              {(modal.type === 'quote' || modal.type === 'carrier') && (
                <motion.div
                  key={`${modal.type}-${modal.step}`}
                  className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[70] overflow-y-auto px-3 pt-20 sm:pt-6 pb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {modal.step === 'form' ? (
                    modal.type === 'quote' ? (
                      <QuoteForm
                        onClose={closeModal}
                        onSuccess={handleFormSuccess('quote')}
                      />
                    ) : (
                      <CarrierRegistrationForm
                        onClose={closeModal}
                        onSuccess={handleFormSuccess('carrier')}
                      />
                    )
                  ) : modal.type === 'carrier' ? (
                    // Carrier registration is FREE - should never reach payment step
                    // If we do, show error message
                    <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-red-600">
                      <h3 className="font-bold mb-2">Error</h3>
                      <p>
                        Carrier registration is completely free. No payment is
                        required.
                      </p>
                      <button
                        onClick={closeModal}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Close
                      </button>
                    </div>
                  ) : user && isEmailVerified ? (
                    // Only quotes can reach payment step (for transport service payment)
                    <PaymentSection
                      email={user.email}
                      formType={modal.type as 'quote'}
                      recordId={modal.recordId}
                      onClose={closeModal}
                    />
                  ) : (
                    <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-red-600">
                      You must verify your email before making a payment.
                    </div>
                  )}
                </motion.div>
              )}

              {modal.type === 'auth' && (
                <motion.div
                  key="auth"
                  className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[70] overflow-y-auto px-3 pt-20 sm:pt-6 pb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full relative">
                    <button
                      onClick={closeModal}
                      aria-label="Close modal"
                      className="absolute top-4 right-4 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    >
                      <FontAwesomeIcon
                        icon={faTimes}
                        className="text-gray-800 text-lg"
                      />
                    </button>
                    <AuthModal onClose={closeModal} />
                  </div>
                </motion.div>
              )}

              {modal.type === 'auth-required' && (
                <motion.div
                  key="auth-required"
                  className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[70] overflow-y-auto px-3 pt-20 sm:pt-6 pb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <AuthRequiredAlert
                    isOpen={true}
                    onClose={closeModal}
                    onOpenAuth={() => setModal({ type: 'auth', step: 'form' })}
                    type={modal.authRequiredFor || 'quote'}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {successMessage && (
              <div className="fixed left-1/2 -translate-x-1/2 bottom-8 z-50">
                <div className="flex items-center px-6 py-3 bg-green-600 text-white rounded shadow-lg animate-fade-in">
                  <svg
                    className="mr-2 text-xl"
                    width="20"
                    height="20"
                    fill="currentColor"
                  >
                    <circle cx="10" cy="10" r="10" />
                  </svg>
                  {successMessage}
                </div>
              </div>
            )}
          </div>

          {/* Chat Components - Only visible on Home for logged-in users */}
          {isHomeRoute && user && (isAdminOrAgent ? <HomeChatManager /> : <HomeChatButton />)}

          {/* CCP Panel - Only for admins/agents */}
          {isAdminOrAgent && (
            <>
              <EmbeddedCCPPanel isOpen={ccpOpen} onToggle={setCcpOpen} />
              <FloatingCCPButton isOpen={ccpOpen} onToggle={setCcpOpen} />
            </>
          )}

          {/* Sistema de Notificaciones */}
          <ToastContainer position="top-center" maxToasts={3} />
        </SessionErrorHandler>
      </NotificationProvider>
    </QueryClientProvider>
  );
}

export default App;
